// Misc
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import withRequest from 'decorators/withRequest';
import debounce from 'lodash/debounce';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import jstz from 'jstz';

// Validation
import { withValidator, Validator } from 'react-vld';
import { validateRequiredString, validateEmail, validateURL,
  validateEqualStrings, validateMinLength } from 'utils/validation';

// Actions
import { createDomainRequest, createDomainRequestAttempt } from 'actions/domainRequest';

// Components
import Helmet from 'components/Helmet';
import Header from 'components/Header';
import ScrollForm, { ScrollFormTextFieldsSection, ScrollFormSelectSection } from 'components/ScrollForm';

const mapDispatchToProps = {
  createDomainRequestAction: createDomainRequest,
  createDomainRequestAttemptAction: createDomainRequestAttempt,
};

const currencyList = [
  { key: 'USD', value: 'USD', text: 'USD' },
  { key: 'HKD', value: 'HKD', text: 'HKD' },
  { key: 'GBP', value: 'GBP', text: 'GBP' },
  { key: 'AUD', value: 'AUD', text: 'AUD' },
  { key: 'CAD', value: 'CAD', text: 'CAD' },
  { key: 'SGD', value: 'SGD', text: 'SGD' },
  { key: 'TWD', value: 'TWD', text: 'TWD' }
];

@withRouter
@withRequest
@withValidator
@connect(null, mapDispatchToProps)
export default class OnboardingPage extends Component {
  static propTypes = {
    validator: PropTypes.object.isRequired,
    observeRequest: PropTypes.func.isRequired,
    getObservedRequest: PropTypes.func.isRequired,
    createDomainRequestAction: PropTypes.func.isRequired,
    createDomainRequestAttemptAction: PropTypes.func.isRequired,
    history: PropTypes.object.isRequired,
  };

  constructor({ validator }) {
    super();

    this.state = {
      entityName: '',
      contactPersonFirstName: '',
      contactPersonLastName: '',
      email: '',
      website: '',
      password: '',
      passwordConfirm: '',
      currency: 'USD',
      timezone: {
        name: jstz.determine().name(),
        offset: -(new Date()).getTimezoneOffset(), // minus to align with moment js
      }
    };

    validator.addRules({
      entityName: new Validator(
        () => validateRequiredString(this.state.entityName)
      ),
      contactPersonFirstName: new Validator(
        () => validateRequiredString(this.state.contactPersonFirstName)
      ),
      contactPersonLastName: new Validator(
        () => validateRequiredString(this.state.contactPersonLastName)
      ),
      email: new Validator(
        () => validateRequiredString(this.state.email),
        () => validateEmail(this.state.email)
      ),
      website: new Validator(
        () => validateURL(this.state.website)
      ),
      password: new Validator(
        () => validateRequiredString(this.state.password),
        () => validateMinLength(this.state.password, 6)
      ),
      currency: new Validator(
        () => validateRequiredString(this.state.currency)
      ),
      passwordConfirm: new Validator(
        () => validateRequiredString(this.state.passwordConfirm),
        () => validateEqualStrings(this.state.password, this.state.passwordConfirm)
      )
    });
  }

  componentDidUpdate(prevProps, prevState) {
    if (prevState.email !== this.state.email) {
      this.handleEmailUpdate();
    }
  }

  componentWillUnmount() {
    this.handleEmailUpdate.cancel();
  }

  updateField(fieldName, value) {
    const { validator } = this.props;
    const fieldValidator = validator.getRule(fieldName);
    this.setState(
      { [fieldName]: value },
      () => fieldValidator.validate()
    );
  }

  handleEmailUpdate = debounce(async () => {
    const { validator, createDomainRequestAttemptAction } = this.props;
    const emailValidator = validator.getRule('email');
    if (emailValidator.isValid) {
      const { entityName, email } = this.state;
      try {
        await createDomainRequestAttemptAction({ entityName, email });
      } catch (error) {
        // continue regardless of error
      }
    }
  }, 1000);

  handleTextFieldChange = () => {
    const { validator } = this.props;
    if (validator.isInvalid) {
      validator.reset({
        shouldComponentUpdate: false,
        shouldResetRuleValidators: false
      });
    }
  };

  handleSubmit = async () => {
    const { validator, observeRequest, createDomainRequestAction, history } = this.props;

    if (validator.validate().isValid) {
      const { entityName, contactPersonFirstName, contactPersonLastName,
        email, website, password, timezone, currency } = this.state;

      try {
        // @todo Find a better way to pass cancelToken to request
        await observeRequest(
          cancelToken => createDomainRequestAction({
            entityName,
            contactPerson: {
              firstName: contactPersonFirstName,
              lastName: contactPersonLastName
            },
            email,
            website,
            password,
            timezone,
            currency
          }, cancelToken),
          { shouldComponentUpdate: { success: false } }
        );
        history.push({ pathname: '/onboarding-success' });
      } catch (error) {
        // continue regardless of error
      }
    }
  };

  renderTextFieldSection(fieldName, props) {
    const { validator } = this.props;
    const fieldValidator = validator.getRule(fieldName);
    return (
      <ScrollFormTextFieldsSection
        {...props}
        defaultValues={[this.state[fieldName]]}
        validators={[fieldValidator]}
        hasValidationError={fieldValidator.isInvalid}
        textFieldProps={[{
          onChange: this.handleTextFieldChange,
          onUpdate: ev => this.updateField(fieldName, ev.target.value)
        }]}
        onSkip={() => this.updateField(fieldName, '')}
      />
    );
  }

  renderSelectSection(fieldName, props) {
    const { validator } = this.props;
    const fieldValidator = validator.getRule(fieldName);
    return (
      <ScrollFormSelectSection
        {...props}
        items={currencyList}
        defaultValues={[this.state[fieldName]]}
        validators={[fieldValidator]}
        hasValidationError={fieldValidator.isInvalid}
        selectProps={[{
          onChange: ev => this.updateField(fieldName, ev.target.value)
        }]}
        onSkip={() => this.updateField(fieldName, '')}
      />
    );
  }

  renderContactPerson() {
    const { validator } = this.props;
    const { contactPersonFirstName, contactPersonLastName } = this.state;
    const firstNameValidator = validator.getRule('contactPersonFirstName');
    const lastNameValidator = validator.getRule('contactPersonLastName');
    return (
      <ScrollFormTextFieldsSection
        title="Contact person"
        defaultValues={[contactPersonFirstName, contactPersonLastName]}
        validators={[firstNameValidator, lastNameValidator]}
        hasValidationError={firstNameValidator.isInvalid || lastNameValidator.isInvalid}
        textFieldProps={[
          {
            label: 'First Name',
            onChange: this.handleTextFieldChange,
            onUpdate: ev => this.updateField('contactPersonFirstName', ev.target.value)
          },
          {
            label: 'Last Name',
            onChange: this.handleTextFieldChange,
            onUpdate: ev => this.updateField('contactPersonLastName', ev.target.value)
          }
        ]}
      />
    );
  }

  renderPasswordSection() {
    const { validator } = this.props;
    const { password, passwordConfirm } = this.state;
    const passwordValidator = validator.getRule('password');
    const passwordConfirmValidator = validator.getRule('passwordConfirm');
    return (
      <ScrollFormTextFieldsSection
        title="Set your password"
        defaultValues={[password, passwordConfirm]}
        validators={[passwordValidator, passwordConfirmValidator]}
        hasValidationError={passwordValidator.isInvalid || passwordConfirmValidator.isInvalid}
        textFieldProps={[
          {
            label: 'Password',
            type: 'password',
            onChange: () => {
              passwordConfirmValidator.reset({ shouldComponentUpdate: false });
              this.handleTextFieldChange();
            },
            onUpdate: ev => this.updateField('password', ev.target.value)
          },
          {
            label: 'Confirm password',
            type: 'password',
            onChange: this.handleTextFieldChange,
            onUpdate: ev => this.updateField('passwordConfirm', ev.target.value)
          }
        ]}
      />
    );
  }

  render() {
    const { validator, getObservedRequest } = this.props;
    return (
      <div>
        <Helmet />
        <Header />

        <ScrollForm
          validator={validator}
          observedRequest={getObservedRequest()}
          submitButtonText="Register"
          onSubmit={this.handleSubmit}
        >
          { this.renderTextFieldSection('entityName', {
            title: 'Tutoring school name'
          })}
          { this.renderContactPerson() }
          { this.renderTextFieldSection('email', {
            title: 'Email'
          })}
          { this.renderSelectSection('currency', {
            title: 'Currency'
          })}
          { this.renderTextFieldSection('website', {
            title: 'Website',
            withSkipButton: true
          })}
          { this.renderPasswordSection() }
        </ScrollForm>
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./src/pages/Onboarding/index.js